import { useEffect, useMemo } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormProvider, type FieldPath, type Resolver, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Card, CardContent } from "@/components/ui/card";
import { EmailField } from "@/components/ui/email-field";
import { Spinner } from "@/components/ui/spinner";
import { RepresentativeFormActions } from "./Components/RepresentativeFormActions";
import { RepresentativeFormError } from "./Components/RepresentativeFormError";
import { RepresentativeNameFields } from "./Components/RepresentativeNameFields";
import { createRepresentativeSchema } from "./schema";
import type {
  RepresentativeFieldName,
  RepresentativeFormProps,
  RepresentativeFormValues,
} from "./types";
import { toErrorMessage } from "./utils";

export const RepresentativeForm = ({
  isFetching,
  errorMessage = "",
  onClickValidate,
  fieldErrors = {},
  onClickClose,
  isOnEdit,
  initialValues = {},
}: RepresentativeFormProps) => {
  const { t } = useTranslation();
  const schema = useMemo(
    () =>
      createRepresentativeSchema(
        t("traderDetails.form.mandatoryFieldBlankError"),
        t("traderDetails.form.invalidEmailError")
      ),
    [t]
  );
  const methods = useForm<RepresentativeFormValues>({
    defaultValues: initialValues,
    mode: "onSubmit",
    reValidateMode: "onChange",
    resolver: zodResolver(schema) as Resolver<RepresentativeFormValues>,
  });
  const {
    clearErrors,
    control,
    formState: { errors, isSubmitting },
    handleSubmit,
    reset,
    setError,
  } = methods;

  useEffect(() => {
    if (Object.keys(fieldErrors).length === 0) {
      clearErrors();
      reset(initialValues);
      return;
    }

    clearErrors();
    Object.entries(fieldErrors).forEach(([fieldName, message]) => {
      setError(fieldName as FieldPath<RepresentativeFormValues>, {
        type: "server",
        message: toErrorMessage(message),
      });
    });
  }, [clearErrors, fieldErrors, initialValues, reset, setError]);

  const getRepresentativeFieldError = useMemo(
    () => (fieldName: RepresentativeFieldName) => {
      const error = errors[fieldName] as { message?: unknown } | undefined;
      return toErrorMessage(error?.message);
    },
    [errors]
  );

  if (isFetching) {
    return (
      <Card className="mb-8 border-neutral-alpha-200 bg-background shadow-none">
        <CardContent className="flex min-h-32 items-center justify-center">
          <Spinner className="size-6 text-primary" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="mb-8 border-neutral-alpha-200 bg-background shadow-none">
      <CardContent>
        <FormProvider {...methods}>
          <form className="space-y-6" noValidate onSubmit={handleSubmit(onClickValidate)}>
            <RepresentativeFormError message={errorMessage} />

            <RepresentativeNameFields
              control={control}
              firstNameLabel={t("aggregatorDetails.administratorModal.firstName")}
              lastNameLabel={t("fieldName.lastName")}
              getFieldError={getRepresentativeFieldError}
            />

            <EmailField
              control={control}
              name="email"
              label={t("traderDetails.form.email")}
              disabled={isOnEdit}
              required
            />

            <RepresentativeFormActions
              isOnEdit={isOnEdit}
              backLabel={t("common.back")}
              createLabel={t("traderDetails.representativeModal.createRepresentative")}
              updateLabel={t("traderDetails.representativeModal.updateRepresentative")}
              isSubmitting={isSubmitting}
              onBack={() => {
                reset({});
                onClickClose();
              }}
            />
          </form>
        </FormProvider>
      </CardContent>
    </Card>
  );
};
